import {
    Configuration,
    TachesEtRappelsDesUtilisateursApi,
    type TachesEtRappelsDesUtilisateursJsonldTacheRead,
    type TachesEtRappelsDesUtilisateursApiApiTachesGetCollectionRequest,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import {
    ApiCollectionResponse,
    FetchCollectionData,
    FetchItemDataType,
    SliceFactory,
} from '@europrocurement/l2d-redux-utils';
import { type UtilisateursConfigurationParameters } from '../types';
import { TACHES_UTILISATEUR_SLICE_NAME, UTILISATEUR_REDUCER_NAME } from '../constants';

export type UtilisateurTachesApiObject = TachesEtRappelsDesUtilisateursJsonldTacheRead &
    Record<string, unknown>;

export type UtilisateurTaches = {
    tache: UtilisateurTachesApiObject;
    id: string;
};

type dateFilters = {
    startDate?: string;
    endDate?: string;
};

// function declared for the slice Factory
const mapperServices = function (tachesUtilisateur: UtilisateurTaches) {
    return {
        ...tachesUtilisateur,
    } as UtilisateurTaches;
};

/**
 * Function that fetch the collection of groupes and store it into the global state Redux
 * @param configuration API configuration parameters
 * @returns the response of API request (get collection/get by id)
 */
export function createTachesUtilisateurSlice(configuration: UtilisateursConfigurationParameters) {
    const conf = new Configuration(configuration);
    const api = new TachesEtRappelsDesUtilisateursApi(conf);

    const fetchTachesUtilisateurCollection: FetchCollectionData<UtilisateurTaches> = ({
        pagination,
        filters,
        orders,
    }) => {
        const {
            tacheTitle,
            tacheIsDone,
            tacheAssignTo,
            tacheCreatedBy,
            tacheExpiredAt,
            tacheCreatedAt,
            tachePriority,
        } = filters;

        let expiredAtAfter: string | undefined;
        let expiredAtBefore: string | undefined;
        let createdAtAfter: string | undefined;
        let createdAtBefore: string | undefined;

        let orderAssignToPrenom: 'asc' | 'desc' | undefined;
        let orderIsDone: 'asc' | 'desc' | undefined;
        let orderTitle: 'asc' | 'desc' | undefined;
        let orderCreatedAt: 'asc' | 'desc' | undefined;
        let orderExpiredAt: 'asc' | 'desc' | undefined;
        let orderPriority: 'asc' | 'desc' | undefined;

        // Range of date to filter
        if (tacheExpiredAt) {
            const filtersDate = tacheExpiredAt as dateFilters;
            const startedAt = filtersDate.startDate && new Date(filtersDate.startDate);
            if (startedAt) {
                startedAt.setHours(0, 0);
                expiredAtAfter = startedAt.toISOString();
            }

            const endedAt = filtersDate.endDate && new Date(filtersDate.endDate);
            if (endedAt) {
                endedAt.setHours(23, 59);
                expiredAtBefore = endedAt.toISOString();
            }
        }

        // Range of date to filter
        if (tacheCreatedAt) {
            const filtersDate = tacheCreatedAt as dateFilters;
            const startedAt = filtersDate.startDate && new Date(filtersDate.startDate);
            if (startedAt) {
                startedAt.setHours(0, 0);
                createdAtAfter = startedAt.toISOString();
            }

            const endedAt = filtersDate.endDate && new Date(filtersDate.endDate);
            if (endedAt) {
                endedAt.setHours(23, 59);
                createdAtBefore = endedAt.toISOString();
            }
        }

        orders.forEach((order) => {
            if (order.field === 'tacheAssignTo') {
                orderAssignToPrenom = order.value;
            }
            if (order.field === 'tacheIsDone') {
                orderIsDone = order.value;
            }
            if (order.field === 'tacheTitle') {
                orderTitle = order.value;
            }
            if (order.field === 'tacheCreatedAt') {
                orderCreatedAt = order.value;
            }
            if (order.field === 'tachePriority') {
                orderPriority = order.value;
            }
            if (order.field === 'tacheExpiredAt') {
                orderExpiredAt = order.value;
            }
        });

        const requestParameters: TachesEtRappelsDesUtilisateursApiApiTachesGetCollectionRequest = {
            page: pagination.page + 1,
            itemsPerPage: 100,
            // Filters
            isDone: tacheIsDone as boolean,
            title: tacheTitle as string,
            assignTo: tacheAssignTo as string,
            createdBy: tacheCreatedBy as string,
            priority: tachePriority as number,
            expiredAtAfter,
            expiredAtBefore,
            createdAtAfter,
            createdAtBefore,
            // Order
            orderAssignToPrenom,
            orderCreatedAt,
            orderExpiredAt,
            orderIsDone,
            orderPriority,
            orderTitle,
        };

        return api.apiTachesGetCollection(requestParameters, undefined).then((res) =>
            // Update Response to return data wrapped in tache property like other task related to entitities
            ({
                ...res.data,
                'hydra:member': res.data['hydra:member'].map((task) => ({
                    tache: task,
                    id: task.id?.toString(),
                })),
            }),
        ) as Promise<ApiCollectionResponse<UtilisateurTaches>>;
    };

    const fetchTachesUtilisateurItem: FetchItemDataType<UtilisateurTaches> = ({ idItem }) =>
        api.apiTacheIdGet({ id: idItem as string }).then(
            // Update Response to return data wrapped in tache property like other task related to entitities
            (res) => ({
                tache: res.data,
            }),
        ) as Promise<UtilisateurTaches>;

    // Use the utilisateur reducer for the constant because it depends to Utilisateur API
    const { slice: tachesUtilisateurSlice, dataSourcesThunks: tachesUtilisateurDataSourcesThunks } =
        SliceFactory.createSlice<UtilisateurTaches, UtilisateurTaches>(
            TACHES_UTILISATEUR_SLICE_NAME,
            UTILISATEUR_REDUCER_NAME,
            [],
            fetchTachesUtilisateurCollection,
            fetchTachesUtilisateurItem,
            mapperServices,
            {},
            undefined,
            [{ field: 'tacheIsDone', value: 'asc' }],
        );
    return { tachesUtilisateurSlice, tachesUtilisateurDataSourcesThunks };
}
